import { PaiementEnLigneSection } from '../paiement-en-ligne/PaiementEnLigneSection.js';
import { PiecesDuCoffre } from './PiecesDuCoffre.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { TotauxFacture } from './TotauxFacture.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import {
  Badge,
  Button,
  Checkbox,
  classesBouton,
  Dialog,
  IconButton,
  Price,
  PriceInput,
  Select,
  VisuallyHidden,
} from '@artosera/ui';
import { formatDate, REGIMES_TVA, type ModeReglement, type RegimeTva } from '@artosera/shared';
import { ArrowLeft, Download, Eye, EyeOff, Lock, Plus, X } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { oeuvresApi, type EditionDeLOeuvre, type OeuvrePresentee } from '../../api/oeuvres.js';
import { estApiError } from '../../api/errors.js';
import {
  useAjouterReglement,
  useCreerAvoir,
  useEmettreFacture,
  useFacture,
  useModifierFacture,
  usePdfFacture,
  useSupprimerReglement,
  ventesApi,
  type CartelLigneFacture,
  type CorpsLigneFacture,
  type LigneFacturePresentee,
} from '../../api/ventes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import { RechercheOeuvre } from './RechercheOeuvre.js';
import { teinteDeLEtatFacture } from './etatUi.js';
import { enregistrerFichier, ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { GardeSaisie } from '../GardeSaisie.js';
import { DefilementHorizontal } from '../../composants/DefilementHorizontal.js';
import { QUANTITE_MAX, validerLigne, type ErreursLigne } from './validationLignes.js';
import styles from './FactureDetailPage.module.css';
import { SuggestionPrixApprouve } from '../presentation/SuggestionPrixApprouve.js';
import { Verrouillable } from '../module/LectureSeule.js';

interface LigneEnCours {
  cleLocale: string;
  oeuvreId?: string;
  exemplaireId?: string;
  /**
   * Édition dont les exemplaires sont suivis un par un : la ligne doit en
   * désigner un (seconde relecture ARTO-51, N7), choisi parmi `exemplaires`.
   */
  exigeExemplaire?: boolean;
  exemplaires?: readonly EditionDeLOeuvre[];
  /**
   * Vrai tant que le prix de cette nouvelle ligne d'œuvre est celui que
   * l'écran PROPOSE (œuvre, puis exemplaire choisi) : il ne part pas, et
   * l'API prend celui de l'exemplaire ou de l'œuvre. Une saisie de
   * l'utilisateur le rend explicite (troisième relecture, constat 7).
   */
  prixPropose?: boolean;
  /** Le prix de l'œuvre, repli d'un exemplaire sans prix propre. */
  prixOeuvreCentimes?: number;
  estOeuvre: boolean;
  libelle: string;
  quantite: number;
  /**
   * Quantité telle que saisie : un champ vidé ou à zéro est signalé, jamais
   * ramené à 1 en silence. Absente tant que la ligne n'a pas été touchée.
   */
  quantiteTexte?: string;
  prixUnitaireCentimes: number;
  remisePourcent: number;
  /** Remise telle que saisie, en pourcents (même principe que `quantiteTexte`). */
  remiseTexte?: string;
  regimeTva: RegimeTva;
  droitDeSuite: boolean;
  prixAchatCentimes?: number;
  /** Le cartel figé de la ligne d'œuvre (7.6) : affichage seulement. */
  cartel?: CartelLigneFacture | null;
}

let compteur = 0;
function cleLocale(): string {
  compteur += 1;
  return `ligne-${String(compteur)}`;
}

function ligneEnCoursDepuisPresentee(ligne: LigneFacturePresentee): LigneEnCours {
  return {
    cleLocale: cleLocale(),
    ...(ligne.oeuvreId ? { oeuvreId: ligne.oeuvreId } : {}),
    ...(ligne.exemplaireId ? { exemplaireId: ligne.exemplaireId } : {}),
    estOeuvre: Boolean(ligne.oeuvreId),
    libelle: ligne.cartel
      ? [ligne.cartel.artiste, ligne.cartel.titre].filter(Boolean).join(' — ')
      : ligne.designation,
    quantite: ligne.quantite,
    prixUnitaireCentimes: ligne.prixUnitaireCentimes,
    remisePourcent: ligne.remisePointsDeBase / 100,
    regimeTva: ligne.regimeTva,
    droitDeSuite: ligne.droitDeSuite,
    ...(ligne.prixAchatCentimes != null ? { prixAchatCentimes: ligne.prixAchatCentimes } : {}),
    cartel: ligne.cartel ?? null,
  };
}

function versCorpsLigne(ligne: LigneEnCours): CorpsLigneFacture {
  return {
    ...(ligne.oeuvreId ? { oeuvreId: ligne.oeuvreId } : {}),
    ...(ligne.exemplaireId ? { exemplaireId: ligne.exemplaireId } : {}),
    ...(ligne.estOeuvre ? {} : { designation: ligne.libelle }),
    quantite: Number(ligne.quantiteTexte ?? ligne.quantite),
    ...(ligne.prixPropose === true ? {} : { prixUnitaireCentimes: ligne.prixUnitaireCentimes }),
    remisePointsDeBase: Math.round(
      Number((ligne.remiseTexte ?? String(ligne.remisePourcent)).replace(',', '.')) * 100,
    ),
    regimeTva: ligne.regimeTva,
    droitDeSuite: ligne.droitDeSuite,
    ...(ligne.prixAchatCentimes !== undefined
      ? { prixAchatCentimes: ligne.prixAchatCentimes }
      : {}),
  };
}

/**
 * Le choix d'un exemplaire : son prix propre devient le prix proposé de la
 * ligne (troisième relecture, constat 7) — sauf si l'utilisateur a déjà saisi
 * le sien.
 */
/** Les erreurs d'une ligne, lues sur ce qui est saisi. */
function erreursDeLigne(ligne: LigneEnCours): ErreursLigne {
  return validerLigne({
    quantite: ligne.quantiteTexte ?? String(ligne.quantite),
    prixUnitaireCentimes: ligne.prixUnitaireCentimes,
    remisePourcent: ligne.remiseTexte ?? String(ligne.remisePourcent),
  });
}

function choisirExemplaire(ligne: LigneEnCours, exemplaireId: string): Partial<LigneEnCours> {
  if (ligne.prixPropose !== true) return { exemplaireId };
  const exemplaire = ligne.exemplaires?.find((candidat) => candidat.id === exemplaireId);
  return {
    exemplaireId,
    prixUnitaireCentimes:
      exemplaire?.prixCentimes ?? ligne.prixOeuvreCentimes ?? ligne.prixUnitaireCentimes,
  };
}

/** Les statuts d'un exemplaire qu'une facture peut encore vendre (D13). */
const STATUTS_VENDABLES: readonly string[] = ['disponible', 'reservee', 'en_depot', 'pretee'];

const MODES_REGLEMENT_UI: readonly ModeReglement[] = [
  'virement',
  'carte',
  'cheque',
  'especes',
  'autre',
];

/**
 * Fiche facture — page entière (3.3, écran 5). En-tête, lignes (éditables
 * seulement au brouillon, § 17.4/17.5), totaux, règlements, gestes (émettre,
 * avoir, PDF, Factur-X), aperçu du PDF dans un panneau.
 */
export function FactureDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie } = useShellContext();

  const fiche = useFacture(galerieSlug, id ?? null);
  const modifier = useModifierFacture(galerieSlug);
  const emettre = useEmettreFacture(galerieSlug);
  const creerAvoir = useCreerAvoir(galerieSlug);

  const [apercuOuvert, setApercuOuvert] = useState(false);
  const [emissionOuverte, setEmissionOuverte] = useState(false);
  const [emiseLe, setEmiseLe] = useState(() => new Date().toISOString().slice(0, 10));
  const [erreurEmission, setErreurEmission] = useState<string | null>(null);
  const [avoirOuvert, setAvoirOuvert] = useState(false);
  const [motifAvoir, setMotifAvoir] = useState('');
  const [erreurAvoir, setErreurAvoir] = useState<string | null>(null);

  const [lignesEnCours, setLignesEnCours] = useState<LigneEnCours[] | null>(null);
  const [erreurLignes, setErreurLignes] = useState<string | null>(null);

  const pdf = usePdfFacture(galerieSlug, id ?? null, {
    enabled: apercuOuvert && (fiche.data?.facture.pdfDisponible ?? false),
  });

  if (fiche.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (fiche.isError || !fiche.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const { facture, reglements, avoirs } = fiche.data;
  const estBrouillon = facture.etat === 'brouillon';
  const estFacture = facture.type === 'facture';
  const peutFacturer = galerieALaction(galerie, 'invoices:write');
  const peutEmettre = galerieALaction(galerie, 'invoices:issue');
  const peutFaireAvoir = galerieALaction(galerie, 'invoices:credit');
  const peutRegler = galerieALaction(galerie, 'payments:write');
  const modifiable = estBrouillon && peutFacturer;

  const lignes = lignesEnCours ?? facture.lignes.map(ligneEnCoursDepuisPresentee);
  const lignesModifiees = lignesEnCours !== null;

  async function ajouterLigneOeuvre(oeuvre: OeuvrePresentee): Promise<void> {
    // Une édition suivie exemplaire par exemplaire ne se vend pas entière : la
    // ligne attend le choix d'un exemplaire encore vendable (N7).
    const suivie = oeuvre.type === 'edition' && oeuvre.tirage?.exemplairesSuivis === true;
    let exemplaires: readonly EditionDeLOeuvre[] = [];
    if (suivie) {
      try {
        const reponse = await dansLaPorteeCourante((appel) =>
          oeuvresApi.editions(galerieSlug, oeuvre.id, appel),
        );
        exemplaires = reponse.editions.filter((edition) =>
          STATUTS_VENDABLES.includes(edition.statut),
        );
      } catch {
        exemplaires = [];
      }
    }
    setLignesEnCours([
      ...lignes,
      {
        cleLocale: cleLocale(),
        oeuvreId: oeuvre.id,
        ...(suivie ? { exigeExemplaire: true, exemplaires } : {}),
        estOeuvre: true,
        libelle: `${oeuvre.artisteNom} — ${oeuvre.titre}`,
        cartel: { artiste: oeuvre.artisteNom, titre: oeuvre.titre },
        quantite: 1,
        prixUnitaireCentimes: oeuvre.prixPublicCentimes ?? 0,
        prixPropose: true,
        prixOeuvreCentimes: oeuvre.prixPublicCentimes ?? 0,
        remisePourcent: 0,
        regimeTva: oeuvre.regimeTva,
        droitDeSuite: oeuvre.droitDeSuite,
        ...(oeuvre.prixAchatCentimes != null
          ? { prixAchatCentimes: oeuvre.prixAchatCentimes }
          : {}),
      },
    ]);
  }

  function ajouterLigneLibre(): void {
    setLignesEnCours([
      ...lignes,
      {
        cleLocale: cleLocale(),
        estOeuvre: false,
        libelle: '',
        quantite: 1,
        prixUnitaireCentimes: 0,
        remisePourcent: 0,
        regimeTva: 'normal',
        droitDeSuite: false,
      },
    ]);
  }

  function retirerLigne(cle: string): void {
    setLignesEnCours(lignes.filter((ligne) => ligne.cleLocale !== cle));
  }

  function modifierLigne(cle: string, partiel: Partial<LigneEnCours>): void {
    setLignesEnCours(
      lignes.map((ligne) => (ligne.cleLocale === cle ? { ...ligne, ...partiel } : ligne)),
    );
  }

  async function enregistrerLignes(): Promise<void> {
    setErreurLignes(null);
    if (lignes.length === 0) {
      setErreurLignes(t('regie:ventes.fiche.aucuneLigne'));
      return;
    }
    if (lignes.some((ligne) => !ligne.estOeuvre && ligne.libelle.trim().length === 0)) {
      setErreurLignes(t('regie:ventes.fiche.designationManquante'));
      return;
    }
    if (lignes.some((ligne) => ligne.exigeExemplaire === true && !ligne.exemplaireId)) {
      setErreurLignes(t('regie:ventes.fiche.exemplaireManquant'));
      return;
    }
    if (lignes.some((ligne) => Object.keys(erreursDeLigne(ligne)).length > 0)) {
      setErreurLignes(t('regie:ventes.fiche.lignesInvalides'));
      return;
    }
    try {
      await modifier.mutateAsync({
        id: facture.id,
        corps: { lignes: lignes.map(versCorpsLigne) },
      });
      setLignesEnCours(null);
    } catch (error) {
      setErreurLignes(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function confirmerEmission(): Promise<void> {
    setErreurEmission(null);
    try {
      // La version AFFICHÉE : un brouillon modifié entre-temps est refusé (N8).
      await emettre.mutateAsync({
        id: facture.id,
        corps: { emiseLe, versionAttendue: facture.version },
      });
      setEmissionOuverte(false);
    } catch (error) {
      setErreurEmission(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function confirmerAvoir(): Promise<void> {
    setErreurAvoir(null);
    try {
      await creerAvoir.mutateAsync({ id: facture.id, corps: { motif: motifAvoir || undefined } });
      setAvoirOuvert(false);
      setMotifAvoir('');
    } catch (error) {
      setErreurAvoir(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  const avecReglements = estFacture && !estBrouillon;
  const avecCoffre =
    !estBrouillon &&
    galerieAModule(galerie, 'coffre') &&
    galerieALaction(galerie, 'documents:read');
  const avecColonne =
    avecReglements || avoirs.length > 0 || (apercuOuvert && facture.pdfDisponible) || avecCoffre;

  return (
    <div className={styles.page} data-ui="invoice-detail">
      <GardeSaisie actif={modifiable && lignesModifiees} />
      <Link to={`/${galerieSlug}/ventes`} className={styles.retour}>
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden />
        {t('regie:ventes.fiche.retour')}
      </Link>

      <div className={styles.barre}>
        <div className={styles.gestes}>
          <Verrouillable action="invoices:issue" ouvert={peutEmettre} applicable={estBrouillon}>
            <Button
              size="sm"
              onClick={() => {
                setErreurEmission(null);
                setEmissionOuverte(true);
              }}
            >
              {t('regie:ventes.fiche.emettre')}
            </Button>
          </Verrouillable>
          <Verrouillable
            action="invoices:credit"
            ouvert={peutFaireAvoir}
            applicable={!estBrouillon && estFacture}
          >
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setErreurAvoir(null);
                setAvoirOuvert(true);
              }}
            >
              {t('regie:ventes.fiche.creerAvoir')}
            </Button>
          </Verrouillable>
          {facture.pdfDisponible ? (
            <Button
              size="sm"
              variant="secondary"
              aria-pressed={apercuOuvert}
              onClick={() => setApercuOuvert((valeur) => !valeur)}
            >
              {apercuOuvert ? (
                <EyeOff size={16} strokeWidth={1.5} aria-hidden />
              ) : (
                <Eye size={16} strokeWidth={1.5} aria-hidden />
              )}
              {t('regie:ventes.fiche.apercu')}
            </Button>
          ) : null}
          {facture.pdfDisponible ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void dansLaPorteeCourante((appel) =>
                  ventesApi.urlPdf(galerieSlug, facture.id, appel),
                ).then(
                  (reponse) => {
                    ouvrirUrlSignee(reponse.url);
                  },
                  () => undefined,
                );
              }}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:ventes.fiche.telechargerPdf')}
            </Button>
          ) : null}
          {facture.facturXDisponible ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void dansLaPorteeCourante((appel) =>
                  ventesApi.facturX(galerieSlug, facture.id, appel),
                ).then(
                  (fichier) => {
                    enregistrerFichier(fichier, `${facture.numero ?? facture.id}.xml`);
                  },
                  () => undefined,
                );
              }}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:ventes.fiche.telechargerFacturX')}
            </Button>
          ) : null}
        </div>

        {!estBrouillon ? (
          <p className={styles.immuable} role="status">
            <Lock size={14} strokeWidth={1.5} aria-hidden />
            {t('ventes:factures.immuable')}
          </p>
        ) : null}
      </div>

      <div
        className={styles.corps}
        data-apercu={apercuOuvert ? 'ouvert' : 'ferme'}
        data-colonne={avecColonne ? 'oui' : 'non'}
      >
        <FeuillePiece
          className={styles.feuille}
          emetteur={galerie.nom ?? ''}
          nature={
            facture.type === 'avoir' ? (
              <Badge tone="neutral">{t('ventes:factures.avoir')}</Badge>
            ) : (
              t('ventes:factures.facture')
            )
          }
          destinataire={{
            legende: t('regie:ventes.colonneContact'),
            nom: facture.contactNom ?? '—',
            complement: facture.factureOrigineId ? (
              <Link
                className={classesBouton('secondary', 'sm')}
                to={`/${galerieSlug}/ventes/${facture.factureOrigineId}`}
              >
                {t('regie:ventes.fiche.voirFactureOrigine')}
              </Link>
            ) : null,
          }}
          numero={facture.numero ?? t('ventes:factures.brouillon')}
          brouillon={!facture.numero}
          tampon={
            <Badge tone={teinteDeLEtatFacture(facture.etat)}>
              {t(`ventes:factures.${facture.etat}`)}
            </Badge>
          }
          meta={
            facture.emiseLe
              ? t('ventes:factures.emise_le', {
                  date: formatDate(new Date(facture.emiseLe), langue, 'long'),
                })
              : null
          }
          etat={facture.etat}
          pied={
            facture.mentions.length > 0
              ? facture.mentions.map((mention) => (
                  <p key={mention.fr}>{mention[langue] ?? mention.fr}</p>
                ))
              : null
          }
        >
          <section className={styles.sectionLignes}>
            <div className={styles.sectionEntete}>
              <h2 className={styles.legendeTitre}>{t('regie:ventes.fiche.lignesTitre')}</h2>
              {modifiable && lignesModifiees ? (
                <div className={styles.actionsLignes}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setLignesEnCours(null);
                      setErreurLignes(null);
                    }}
                  >
                    {t('commun:actions.annuler')}
                  </Button>
                  <Button
                    size="sm"
                    loading={modifier.isPending}
                    onClick={() => void enregistrerLignes()}
                  >
                    {t('commun:actions.enregistrer')}
                  </Button>
                </div>
              ) : null}
            </div>

            <DefilementHorizontal className={styles.tableauEnveloppe}>
              <table className={styles.tableau} data-modifiable={modifiable ? 'oui' : 'non'}>
                <thead>
                  <tr>
                    <th scope="col">{t('regie:ventes.fiche.colonneDesignation')}</th>
                    <th scope="col" className={styles.colCentre}>
                      {t('regie:ventes.fiche.colonneQuantite')}
                    </th>
                    <th scope="col" className={styles.colMontant}>
                      {t('regie:ventes.fiche.colonnePrixHt')}
                    </th>
                    <th scope="col" className={styles.colMontant}>
                      {t('regie:ventes.fiche.colonneRemise')}
                    </th>
                    <th scope="col">{t('regie:ventes.fiche.colonneRegime')}</th>
                    <th scope="col" className={styles.colCentre}>
                      {t('regie:ventes.fiche.colonneDroitDeSuite')}
                    </th>
                    {modifiable ? (
                      <th scope="col">
                        <VisuallyHidden>{t('commun:actions.supprimer')}</VisuallyHidden>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((ligne) => {
                    const erreurs = erreursDeLigne(ligne);
                    return (
                      <tr key={ligne.cleLocale}>
                        <td className={styles.celDesignation}>
                          {ligne.estOeuvre && modifiable && ligne.exigeExemplaire === true ? (
                            <span className={styles.ligneExemplaire}>
                              <CartelDeLigne ligne={ligne} />
                              <Select
                                aria-label={t('regie:ventes.fiche.colonneExemplaire')}
                                placeholder={t('regie:ventes.fiche.choisirExemplaire')}
                                {...(ligne.exemplaireId ? { value: ligne.exemplaireId } : {})}
                                onValueChange={(valeur) =>
                                  modifierLigne(ligne.cleLocale, choisirExemplaire(ligne, valeur))
                                }
                                options={(ligne.exemplaires ?? []).map((edition) => ({
                                  value: edition.id,
                                  label: edition.numero,
                                }))}
                              />
                            </span>
                          ) : ligne.estOeuvre || !modifiable ? (
                            <CartelDeLigne ligne={ligne} />
                          ) : (
                            <input
                              className={styles.saisieDesignation}
                              aria-label={t('regie:ventes.fiche.colonneDesignation')}
                              value={ligne.libelle}
                              onChange={(event) =>
                                modifierLigne(ligne.cleLocale, { libelle: event.target.value })
                              }
                            />
                          )}
                        </td>
                        <td
                          className={styles.colCentre}
                          data-libelle={t('regie:ventes.fiche.colonneQuantite')}
                        >
                          {modifiable ? (
                            <>
                              <input
                                type="number"
                                min={1}
                                max={QUANTITE_MAX}
                                step={1}
                                className={styles.saisieNombre}
                                aria-label={t('regie:ventes.fiche.colonneQuantite')}
                                aria-invalid={erreurs.quantite ? true : undefined}
                                value={ligne.quantiteTexte ?? String(ligne.quantite)}
                                onChange={(event) =>
                                  modifierLigne(ligne.cleLocale, {
                                    quantiteTexte: event.target.value,
                                  })
                                }
                              />
                              {erreurs.quantite ? (
                                <span className={styles.erreurLigne} role="alert">
                                  {t(erreurs.quantite, { max: QUANTITE_MAX })}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            ligne.quantite
                          )}
                        </td>
                        <td
                          className={styles.colMontant}
                          data-libelle={t('regie:ventes.fiche.colonnePrixHt')}
                        >
                          {modifiable ? (
                            <>
                              <PriceInput
                                aria-label={t('regie:ventes.fiche.colonnePrixHt')}
                                aria-invalid={erreurs.prix ? true : undefined}
                                cents={ligne.prixUnitaireCentimes}
                                onCentsChange={(cents) =>
                                  modifierLigne(ligne.cleLocale, {
                                    prixUnitaireCentimes: cents,
                                    prixPropose: false,
                                  })
                                }
                                lang={langue}
                                currency={facture.devise}
                              />
                              {estFacture &&
                              galerieAModule(galerie, 'facturation') &&
                              ligne.oeuvreId &&
                              (!ligne.exigeExemplaire || ligne.exemplaireId) ? (
                                <SuggestionPrixApprouve
                                  contactId={facture.contactId}
                                  oeuvreId={ligne.oeuvreId}
                                  exemplaireId={ligne.exemplaireId}
                                  devise={facture.devise}
                                  onAppliquer={(prixUnitaireCentimes) =>
                                    modifierLigne(ligne.cleLocale, {
                                      prixUnitaireCentimes,
                                      prixPropose: false,
                                      remisePourcent: 0,
                                      remiseTexte: undefined,
                                    })
                                  }
                                />
                              ) : null}
                              {erreurs.prix ? (
                                <span className={styles.erreurLigne} role="alert">
                                  {t(erreurs.prix)}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <Price
                              cents={ligne.prixUnitaireCentimes}
                              lang={langue}
                              currency={facture.devise}
                            />
                          )}
                        </td>
                        <td
                          className={`${styles.colMontant} ${styles.celChiffre}`}
                          data-libelle={t('regie:ventes.fiche.colonneRemise')}
                        >
                          {modifiable ? (
                            <>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step="any"
                                className={styles.saisieNombre}
                                aria-label={t('regie:ventes.fiche.colonneRemise')}
                                aria-invalid={erreurs.remise ? true : undefined}
                                value={ligne.remiseTexte ?? String(ligne.remisePourcent)}
                                onChange={(event) =>
                                  modifierLigne(ligne.cleLocale, {
                                    remiseTexte: event.target.value,
                                  })
                                }
                              />
                              {erreurs.remise ? (
                                <span className={styles.erreurLigne} role="alert">
                                  {t(erreurs.remise)}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            `${String(ligne.remisePourcent)} %`
                          )}
                        </td>
                        <td
                          className={styles.celRegime}
                          data-libelle={t('regie:ventes.fiche.colonneRegime')}
                        >
                          {modifiable ? (
                            <Select
                              aria-label={t('regie:ventes.fiche.colonneRegime')}
                              value={ligne.regimeTva}
                              onValueChange={(valeur) =>
                                modifierLigne(ligne.cleLocale, { regimeTva: valeur as RegimeTva })
                              }
                              options={REGIMES_TVA.map((code) => ({
                                value: code,
                                label: t(`ventes:regimes.${code}`),
                              }))}
                            />
                          ) : (
                            t(`ventes:regimes.${ligne.regimeTva}`)
                          )}
                        </td>
                        <td
                          className={styles.colCentre}
                          data-libelle={t('regie:ventes.fiche.colonneDroitDeSuite')}
                        >
                          {modifiable ? (
                            <Checkbox
                              label=""
                              aria-label={t('regie:ventes.fiche.colonneDroitDeSuite')}
                              checked={ligne.droitDeSuite}
                              onCheckedChange={(valeur) =>
                                modifierLigne(ligne.cleLocale, { droitDeSuite: valeur === true })
                              }
                            />
                          ) : ligne.droitDeSuite ? (
                            t('regie:ventes.fiche.oui')
                          ) : (
                            '—'
                          )}
                        </td>
                        {modifiable ? (
                          <td className={styles.celRetirer}>
                            <IconButton
                              variant="outline"
                              icon={<X aria-hidden />}
                              aria-label={t('commun:actions.supprimer')}
                              size="sm"
                              onClick={() => retirerLigne(ligne.cleLocale)}
                            />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DefilementHorizontal>

            {modifiable ? (
              <div className={styles.ajoutLigne}>
                <div className={styles.ajoutRecherche}>
                  <RechercheOeuvre
                    galerieSlug={galerieSlug}
                    onChoisir={(oeuvre) => void ajouterLigneOeuvre(oeuvre)}
                  />
                </div>
                <Button variant="secondary" size="sm" onClick={ajouterLigneLibre}>
                  <Plus size={16} strokeWidth={1.5} aria-hidden />
                  {t('regie:ventes.fiche.ligneLibre')}
                </Button>
              </div>
            ) : null}

            {erreurLignes ? (
              <p className={styles.erreur} role="alert">
                {erreurLignes}
              </p>
            ) : null}
          </section>

          <section className={styles.sectionTotaux}>
            <h2 className={styles.legendeTitre}>{t('regie:ventes.fiche.totauxTitre')}</h2>
            <TotauxFacture facture={facture} langue={langue} />
            {modifiable && lignesModifiees ? (
              <p className={styles.totauxPeriMes} role="status">
                {t('regie:ventes.fiche.totauxApresEnregistrement')}
              </p>
            ) : null}
          </section>
        </FeuillePiece>

        {avecColonne ? (
          <div className={styles.colonne}>
            {apercuOuvert && facture.pdfDisponible ? (
              <aside className={styles.apercuPanneau} aria-label={t('regie:ventes.fiche.apercu')}>
                {pdf.isLoading ? (
                  <p className={styles.vide}>{t('commun:chargement')}</p>
                ) : pdf.isError || !pdf.data ? (
                  <p className={styles.vide} role="alert">
                    {estApiError(pdf.error) ? pdf.error.message : t('commun:erreurReseau')}
                  </p>
                ) : (
                  <iframe
                    className={styles.apercuIframe}
                    src={pdf.data.url}
                    title={t('regie:ventes.fiche.apercu')}
                  />
                )}
              </aside>
            ) : null}

            <PaiementEnLigneSection
              galerieSlug={galerieSlug}
              galerie={galerie}
              factureId={facture.id}
              type={facture.type}
              etat={facture.etat}
            />

            {avecReglements ? (
              <ReglementsSection
                galerieSlug={galerieSlug}
                factureId={facture.id}
                devise={facture.devise}
                langue={langue}
                reglements={reglements}
                resteADevoirCentimes={facture.resteADevoirCentimes}
                totalTtcCentimes={facture.totaux.totalTtcCentimes}
                peutRegler={peutRegler}
              />
            ) : null}

            {avoirs.length > 0 ? (
              <section className={styles.carte}>
                <h2 className={styles.carteTitre}>{t('regie:ventes.fiche.avoirsTitre')}</h2>
                <ul className={styles.listeAvoirs}>
                  {avoirs.map((avoir) => (
                    <li key={avoir.id}>
                      <Link className={styles.lienAvoir} to={`/${galerieSlug}/ventes/${avoir.id}`}>
                        {avoir.numero ?? avoir.id}
                      </Link>
                      <Price
                        cents={avoir.totalTtcCentimes}
                        lang={langue}
                        currency={facture.devise}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {avecCoffre ? (
              <PiecesDuCoffre galerieSlug={galerieSlug} factureId={facture.id} />
            ) : null}
          </div>
        ) : null}
      </div>

      <Dialog
        open={emissionOuverte}
        onOpenChange={setEmissionOuverte}
        title={t('regie:ventes.fiche.emettreConfirmerTitre')}
        description={t('regie:ventes.fiche.emettreConfirmerTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEmissionOuverte(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button onClick={() => void confirmerEmission()} loading={emettre.isPending}>
              {t('regie:ventes.fiche.emettre')}
            </Button>
          </>
        }
      >
        <Field label={t('regie:ventes.fiche.emiseLeChamp')} htmlFor="emission-date">
          <input
            id="emission-date"
            type="date"
            className={styles.saisieDate}
            value={emiseLe}
            onChange={(event) => setEmiseLe(event.target.value)}
          />
        </Field>
        {erreurEmission ? (
          <p className={styles.erreur} role="alert">
            {erreurEmission}
          </p>
        ) : null}
      </Dialog>

      <Dialog
        open={avoirOuvert}
        onOpenChange={setAvoirOuvert}
        title={t('regie:ventes.fiche.avoirConfirmerTitre')}
        description={t('regie:ventes.fiche.avoirConfirmerTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAvoirOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              onClick={() => void confirmerAvoir()}
              loading={creerAvoir.isPending}
            >
              {t('regie:ventes.fiche.creerAvoir')}
            </Button>
          </>
        }
      >
        <Field label={t('regie:ventes.fiche.motifAvoirChamp')} htmlFor="avoir-motif">
          <input
            id="avoir-motif"
            className={styles.saisieDesignation}
            value={motifAvoir}
            onChange={(event) => setMotifAvoir(event.target.value)}
          />
        </Field>
        {erreurAvoir ? (
          <p className={styles.erreur} role="alert">
            {erreurAvoir}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}

/**
 * La désignation d'une ligne : le cartel figé quand la ligne porte une œuvre
 * (artiste, titre en italique, année, technique, dimensions, inventaire), le
 * libellé seul sinon — comme sur la facture imprimée.
 */
function CartelDeLigne({ ligne }: { ligne: LigneEnCours }): JSX.Element {
  const cartel = ligne.cartel;
  if (!ligne.estOeuvre || !cartel || (!cartel.artiste && !cartel.titre)) {
    return <span className={styles.libelleLibre}>{ligne.libelle}</span>;
  }
  return (
    <ObjetCartel
      titre={cartel.titre ?? ligne.libelle}
      artiste={cartel.artiste}
      annee={cartel.annee}
      precisions={[cartel.technique, cartel.dimensions, cartel.edition]}
      numeroInventaire={cartel.numeroInventaire}
    />
  );
}

function ReglementsSection({
  galerieSlug,
  factureId,
  devise,
  langue,
  reglements,
  resteADevoirCentimes,
  totalTtcCentimes,
  peutRegler,
}: {
  galerieSlug: string;
  factureId: string;
  devise: string;
  langue: 'fr' | 'en';
  reglements: readonly {
    id: string;
    montantCentimes: number;
    mode: string;
    date: string;
    reference: string | null;
  }[];
  resteADevoirCentimes: number;
  totalTtcCentimes: number;
  peutRegler: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'ventes', 'commun']);
  const ajouter = useAjouterReglement(galerieSlug);
  const supprimer = useSupprimerReglement(galerieSlug);

  const [ouvert, setOuvert] = useState(false);
  const [montant, setMontant] = useState(resteADevoirCentimes);
  const [mode, setMode] = useState<ModeReglement>('virement');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [erreur, setErreur] = useState<string | null>(null);

  async function soumettre(): Promise<void> {
    setErreur(null);
    try {
      await ajouter.mutateAsync({ factureId, corps: { montantCentimes: montant, mode, date } });
      setOuvert(false);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  const regle = Math.max(0, totalTtcCentimes - resteADevoirCentimes);
  const part = totalTtcCentimes > 0 ? Math.min(100, (regle / totalTtcCentimes) * 100) : 0;

  return (
    <section className={styles.carte}>
      <div className={styles.carteEntete}>
        <h2 className={styles.carteTitre}>{t('ventes:reglements.titre')}</h2>
        <Verrouillable
          action="payments:write"
          ouvert={peutRegler}
          applicable={resteADevoirCentimes > 0}
        >
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setMontant(resteADevoirCentimes);
              setErreur(null);
              setOuvert(true);
            }}
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:ventes.fiche.ajouterReglement')}
          </Button>
        </Verrouillable>
      </div>

      <div className={styles.solde} data-solde={resteADevoirCentimes > 0 ? 'du' : 'regle'}>
        <span className={styles.soldeLibelle}>{t('regie:ventes.fiche.resteADevoir')}</span>
        <Price cents={resteADevoirCentimes} lang={langue} currency={devise} size="lg" />
        <span className={styles.jauge} aria-hidden>
          <span style={{ inlineSize: `${String(part)}%` }} />
        </span>
      </div>

      {reglements.length === 0 ? (
        <p className={styles.videSection}>{t('regie:ventes.fiche.aucunReglement')}</p>
      ) : (
        <ul className={styles.listeReglements}>
          {reglements.map((reglement) => (
            <li key={reglement.id}>
              <span className={styles.reglementDate}>
                {formatDate(new Date(reglement.date), langue, 'court')}
              </span>
              <span className={styles.reglementMode}>
                {t(`ventes:reglements.${reglement.mode}`)}
              </span>
              <span className={styles.reglementMontant}>
                <Price cents={reglement.montantCentimes} lang={langue} currency={devise} />
              </span>
              {peutRegler ? (
                <IconButton
                  variant="outline"
                  icon={<X aria-hidden />}
                  aria-label={t('regie:ventes.fiche.supprimerReglement')}
                  size="sm"
                  onClick={() => supprimer.mutate({ factureId, reglementId: reglement.id })}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={ouvert}
        onOpenChange={setOuvert}
        title={t('regie:ventes.fiche.ajouterReglement')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button onClick={() => void soumettre()} loading={ajouter.isPending}>
              {t('commun:actions.ajouter')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaireReglement}>
          <Field label={t('regie:ventes.fiche.montantChamp')} htmlFor="reglement-montant">
            <PriceInput
              id="reglement-montant"
              cents={montant}
              onCentsChange={setMontant}
              lang={langue}
              currency={devise}
            />
          </Field>
          <Field label={t('regie:ventes.fiche.modeChamp')} htmlFor="reglement-mode">
            <Select
              id="reglement-mode"
              aria-label={t('regie:ventes.fiche.modeChamp')}
              value={mode}
              onValueChange={(valeur) => setMode(valeur as ModeReglement)}
              options={MODES_REGLEMENT_UI.map((code) => ({
                value: code,
                label: t(`ventes:reglements.${code}`),
              }))}
            />
          </Field>
          <Field label={t('regie:ventes.fiche.dateChamp')} htmlFor="reglement-date">
            <input
              id="reglement-date"
              type="date"
              className={styles.saisieDate}
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </div>
      </Dialog>
    </section>
  );
}
